import * as THREE from "three";
import { getSymbol3DPlanScale } from "../../../../../utils/symbolFlipState";
import { isSymbolAspectRatioLocked } from "../../../../../utils/symbolAspectRatio";
import {
  ShapePrimitiveKind,
  SymbolType,
  WallType,
  isCircleSymbolType,
  isPolygonSymbolType,
  isSquareSymbolType,
  isTriangleSymbolType,
} from "../../../../../types/floorplannerTypes";
import { Product3DAssetBounds } from "../../../products/product3DTypes";

export const DEFAULT_SHAPE_3D_THICKNESS = 0.05;
const CIRCLE_SEGMENTS = 48;
const EPSILON = 1e-8;

export type ShapePrimitiveDimensions = {
  width: number;
  depth: number;
};

export type ShapePrimitiveGeometryDefinition = {
  kind: ShapePrimitiveKind;
  points: [number, number][];
  dimensions: ShapePrimitiveDimensions;
  thickness: number;
  baseElevation: number;
};

export function isShapePrimitiveSymbol(
  symbol: SymbolType | undefined,
): boolean {
  return (
    isSquareSymbolType(symbol) ||
    isCircleSymbolType(symbol) ||
    isTriangleSymbolType(symbol) ||
    isPolygonSymbolType(symbol)
  );
}

export function getShapePrimitiveKind(
  symbol: SymbolType,
): ShapePrimitiveKind | null {
  if (isSquareSymbolType(symbol)) return "square";
  if (isCircleSymbolType(symbol)) return "circle";
  if (isTriangleSymbolType(symbol)) return "triangle";
  if (isPolygonSymbolType(symbol)) return symbol.polygonKind;
  return symbol.threeD?.shapeKind ?? null;
}

export function createShapePrimitiveGeometryDefinition(
  symbol: SymbolType,
): ShapePrimitiveGeometryDefinition | null {
  const kind = getShapePrimitiveKind(symbol);
  if (!kind) return null;

  const dimensions = getShapePrimitiveDimensions(symbol);
  if (!dimensions) return null;

  return {
    kind,
    dimensions,
    points: createShapePrimitive2DPoints(kind, dimensions),
    thickness: getShapePrimitiveThickness(symbol),
    baseElevation: getShapePrimitiveBaseElevation(symbol),
  };
}

export function getShapePrimitiveDimensions(
  symbol: SymbolType,
): ShapePrimitiveDimensions | null {
  if (isSquareSymbolType(symbol)) {
    return sanitizeDimensions(symbol.squareWidth, symbol.squareHeight);
  }
  if (isCircleSymbolType(symbol)) {
    return sanitizeDimensions(symbol.circleWidth, symbol.circleHeight);
  }
  if (isTriangleSymbolType(symbol)) {
    return sanitizeDimensions(symbol.triangleWidth, symbol.triangleHeight);
  }
  if (isPolygonSymbolType(symbol)) {
    return sanitizeDimensions(symbol.polygonWidth, symbol.polygonHeight);
  }

  const width = symbol.threeD?.dimensions?.width;
  const depth = symbol.threeD?.dimensions?.depth;
  return sanitizeDimensions(width, depth);
}

export function getShapePrimitiveThickness(symbol: SymbolType): number {
  const explicitHeight = symbol.threeD?.height;
  return isPositiveFiniteNumber(explicitHeight)
    ? explicitHeight
    : DEFAULT_SHAPE_3D_THICKNESS;
}

export function getShapePrimitiveBaseElevation(symbol: SymbolType): number {
  const baseElevation = symbol.threeD?.baseElevation;
  return typeof baseElevation === "number" && Number.isFinite(baseElevation)
    ? baseElevation
    : 0;
}

export function createShapePrimitive2DPoints(
  kind: ShapePrimitiveKind,
  dimensions: ShapePrimitiveDimensions,
): [number, number][] {
  const halfWidth = dimensions.width / 2;
  const halfDepth = dimensions.depth / 2;

  if (kind === "square") {
    return [
      [-halfWidth, -halfDepth],
      [halfWidth, -halfDepth],
      [halfWidth, halfDepth],
      [-halfWidth, halfDepth],
    ];
  }

  if (kind === "triangle") {
    return [
      [0, halfDepth],
      [-halfWidth, -halfDepth],
      [halfWidth, -halfDepth],
    ];
  }

  if (kind === "circle") {
    return createEllipsePoints(dimensions.width, dimensions.depth);
  }

  return createRegularPolygonPoints(getPolygonSideCount(kind), dimensions);
}

export function createShapePrimitiveWorldMatrix(
  symbol: SymbolType,
  walls: readonly WallType[] = [],
): THREE.Matrix4 {
  const definition = createShapePrimitiveGeometryDefinition(symbol);
  const placementMode = symbol.threeD?.placementMode ?? "floor";
  const baseElevation =
    definition?.baseElevation ?? getShapePrimitiveBaseElevation(symbol);

  if (placementMode === "wall") {
    const wall = walls.find(
      (candidate) => candidate.id === symbol.threeD?.wallMount?.wallId,
    );
    if (wall) {
      return createWallMountedShapeMatrix(symbol, wall, baseElevation);
    }
  }

  const baseQuaternion = new THREE.Quaternion().setFromEuler(
    new THREE.Euler(0, 0, symbol.rotation ?? 0),
  );
  const rotationQuaternion =
    createShapePrimitiveLocalRotationQuaternion(symbol);
  const matrix = new THREE.Matrix4();
  matrix.compose(
    new THREE.Vector3(symbol.position.x, symbol.position.y, baseElevation),
    baseQuaternion.multiply(rotationQuaternion),
    new THREE.Vector3(1, 1, 1),
  );
  return matrix;
}

export function createShapePrimitiveWorldVertices(
  symbol: SymbolType,
  walls: readonly WallType[] = [],
): THREE.Vector3[] {
  const definition = createShapePrimitiveGeometryDefinition(symbol);
  if (!definition) return [];

  const matrix = createShapePrimitiveWorldMatrix(symbol, walls);
  const vertices: THREE.Vector3[] = [];
  definition.points.forEach(([x, y]) => {
    vertices.push(new THREE.Vector3(x, y, 0).applyMatrix4(matrix));
    vertices.push(
      new THREE.Vector3(x, y, definition.thickness).applyMatrix4(matrix),
    );
  });
  return vertices;
}

export function createProjectedShapePrimitiveFootprintPolygon(
  symbol: SymbolType,
  walls: readonly WallType[] = [],
): THREE.Vector2[] | null {
  const vertices = createShapePrimitiveWorldVertices(symbol, walls);
  if (vertices.length < 3) return null;
  return createConvexHull(
    vertices.map((vertex) => new THREE.Vector2(vertex.x, vertex.y)),
  );
}

export function shouldShowShapePrimitiveProjection(
  symbol: SymbolType,
): boolean {
  const placementMode = symbol.threeD?.placementMode ?? "floor";
  const pitch = symbol.threeD?.rotation3D?.pitch ?? 0;
  const roll = symbol.threeD?.rotation3D?.roll ?? 0;
  const yaw = symbol.threeD?.rotation3D?.yaw ?? 0;
  const baseElevation = getShapePrimitiveBaseElevation(symbol);
  return (
    placementMode !== "floor" ||
    Math.abs(pitch) > 1e-4 ||
    Math.abs(roll) > 1e-4 ||
    Math.abs(yaw) > 1e-4 ||
    baseElevation > 1e-4
  );
}

export function getDefaultShape3DMetadata(kind: ShapePrimitiveKind) {
  return {
    shapeKind: kind,
    placementMode: "floor" as const,
    height: DEFAULT_SHAPE_3D_THICKNESS,
    baseElevation: 0,
    collision: "box" as const,
    rotation3D: {
      pitch: 0,
      roll: 0,
    },
  };
}

function createWallMountedShapeMatrix(
  symbol: SymbolType,
  wall: WallType,
  baseElevation: number,
): THREE.Matrix4 {
  const dimensions = getShapePrimitiveDimensions(symbol);
  const direction = wall.end.clone().sub(wall.start);
  if (direction.lengthSq() <= EPSILON) {
    return createShapePrimitiveWorldMatrix(
      { ...symbol, threeD: { ...symbol.threeD, placementMode: "floor" } },
      [],
    );
  }

  const length = direction.length();
  const tangent2 = direction.clone().normalize();
  const side = symbol.threeD?.wallMount?.side ?? "left";
  const normal2 = new THREE.Vector2(-tangent2.y, tangent2.x).multiplyScalar(
    side === "left" ? 1 : -1,
  );
  const offset = THREE.MathUtils.clamp(
    symbol.threeD?.wallMount?.offset ??
      projectPointDistanceOnWall(symbol.position, wall),
    0,
    length,
  );
  const distanceFromWall = symbol.threeD?.wallMount?.distanceFromWall ?? 0;
  const center = wall.start
    .clone()
    .add(tangent2.multiplyScalar(offset))
    .add(normal2.clone().multiplyScalar(distanceFromWall));

  const xAxis = new THREE.Vector3(
    direction.x / length,
    direction.y / length,
    0,
  );
  const yAxis = new THREE.Vector3(0, 0, 1);
  const zAxis = new THREE.Vector3(normal2.x, normal2.y, 0);
  const verticalCenter = baseElevation + (dimensions?.depth ?? 0) / 2;
  const basis = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
  const baseQuaternion = new THREE.Quaternion().setFromRotationMatrix(basis);
  const matrix = new THREE.Matrix4();
  matrix.compose(
    new THREE.Vector3(center.x, center.y, verticalCenter),
    baseQuaternion.multiply(
      createShapePrimitiveLocalRotationQuaternion(symbol),
    ),
    new THREE.Vector3(1, 1, 1),
  );
  return matrix;
}

function createShapePrimitiveLocalRotationQuaternion(
  symbol: SymbolType,
): THREE.Quaternion {
  return new THREE.Quaternion().setFromEuler(
    new THREE.Euler(
      symbol.threeD?.rotation3D?.pitch ?? 0,
      symbol.threeD?.rotation3D?.roll ?? 0,
      symbol.threeD?.rotation3D?.yaw ?? 0,
    ),
  );
}

function projectPointDistanceOnWall(
  point: THREE.Vector2,
  wall: WallType,
): number {
  const segment = wall.end.clone().sub(wall.start);
  const lengthSq = segment.lengthSq();
  if (lengthSq <= EPSILON) return 0;
  return THREE.MathUtils.clamp(
    point.clone().sub(wall.start).dot(segment.normalize()),
    0,
    Math.sqrt(lengthSq),
  );
}

function createEllipsePoints(width: number, depth: number): [number, number][] {
  const points: [number, number][] = [];
  for (let index = 0; index < CIRCLE_SEGMENTS; index += 1) {
    const angle = (index / CIRCLE_SEGMENTS) * Math.PI * 2;
    points.push([(Math.cos(angle) * width) / 2, (Math.sin(angle) * depth) / 2]);
  }
  return points;
}

function createRegularPolygonPoints(
  sides: 5 | 6 | 8,
  dimensions: ShapePrimitiveDimensions,
): [number, number][] {
  const points: [number, number][] = [];
  const startAngle = sides % 2 === 0 ? Math.PI / sides : -Math.PI / 2;
  for (let index = 0; index < sides; index += 1) {
    const angle = startAngle + (index / sides) * Math.PI * 2;
    points.push([
      (Math.cos(angle) * dimensions.width) / 2,
      (Math.sin(angle) * dimensions.depth) / 2,
    ]);
  }
  return points;
}

function getPolygonSideCount(kind: ShapePrimitiveKind): 5 | 6 | 8 {
  if (kind === "pentagon") return 5;
  if (kind === "hexagon") return 6;
  return 8;
}

function sanitizeDimensions(
  width: number | undefined,
  depth: number | undefined,
): ShapePrimitiveDimensions | null {
  if (!isPositiveFiniteNumber(width) || !isPositiveFiniteNumber(depth)) {
    return null;
  }
  return { width, depth };
}

/**
 * Computes the 2D convex hull of a point set using the monotone chain
 * (Andrew's) algorithm, returned in counter-clockwise order.
 *
 * Only outer boundary points are kept — unlike a plain angular sort around
 * the centroid, this correctly discards interior points instead of weaving
 * them into the ring (which would otherwise produce a self-intersecting or
 * "star"-shaped outline for point sets with interior points, e.g. the top
 * and bottom faces of a tilted extruded box).
 */
function createConvexHull(points: THREE.Vector2[]): THREE.Vector2[] {
  const unique = uniqueFinitePoints(points);
  if (unique.length < 3) return unique;

  const sorted = unique
    .slice()
    .sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));

  const cross = (o: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const lower: THREE.Vector2[] = [];
  sorted.forEach((point) => {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0
    ) {
      lower.pop();
    }
    lower.push(point);
  });

  const upper: THREE.Vector2[] = [];
  for (let index = sorted.length - 1; index >= 0; index -= 1) {
    const point = sorted[index];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0
    ) {
      upper.pop();
    }
    upper.push(point);
  }

  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

function uniqueFinitePoints(points: THREE.Vector2[]): THREE.Vector2[] {
  const unique: THREE.Vector2[] = [];
  points.forEach((point) => {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
    if (
      unique.some(
        (candidate) => candidate.distanceToSquared(point) <= EPSILON * EPSILON,
      )
    ) {
      return;
    }
    unique.push(point.clone());
  });
  return unique;
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * Returns all local-space corner vertices of the shape primitive's extruded
 * geometry: each XY contour point at both Z=0 and Z=thickness.
 */
export function getShapePrimitiveLocalCorners(
  symbol: SymbolType,
): THREE.Vector3[] {
  const definition = createShapePrimitiveGeometryDefinition(symbol);
  if (!definition) return [];
  const corners: THREE.Vector3[] = [];
  definition.points.forEach(([x, y]) => {
    corners.push(new THREE.Vector3(x, y, 0));
    corners.push(new THREE.Vector3(x, y, definition.thickness));
  });
  return corners;
}

/**
 * Computes the minimum local-space Z among {@link localCorners} after applying
 * pitch (X-axis) and roll (Y-axis) rotation.
 */
function computeMinLocalZAfterRotation(
  localCorners: THREE.Vector3[],
  pitch: number,
  roll: number,
): number {
  const rotMat = new THREE.Matrix4().makeRotationFromEuler(
    new THREE.Euler(pitch, roll, 0, "XYZ"),
  );
  let minZ = Infinity;
  const tmp = new THREE.Vector3();
  localCorners.forEach((corner) => {
    tmp.copy(corner).applyMatrix4(rotMat);
    if (tmp.z < minZ) minZ = tmp.z;
  });
  return minZ;
}

/**
 * Computes the effective "distance from floor" for a shape primitive.
 *
 * Unlike the raw {@link getShapePrimitiveBaseElevation} (the pivot's Z), this
 * accounts for pitch and roll rotation: when the shape is tilted, its lowest
 * physical point may lie below the pivot, yielding a negative value.
 */
export function getShapePrimitiveEffectiveFloorDistance(
  symbol: SymbolType,
): number {
  const baseElevation = getShapePrimitiveBaseElevation(symbol);
  const pitch = symbol.threeD?.rotation3D?.pitch ?? 0;
  const roll = symbol.threeD?.rotation3D?.roll ?? 0;
  if (Math.abs(pitch) < 1e-8 && Math.abs(roll) < 1e-8) {
    return baseElevation;
  }
  const corners = getShapePrimitiveLocalCorners(symbol);
  if (corners.length === 0) return baseElevation;
  const minLocalZ = computeMinLocalZAfterRotation(corners, pitch, roll);
  return Number.isFinite(minLocalZ) ? baseElevation + minLocalZ : baseElevation;
}

/**
 * Back-computes the raw baseElevation so that a shape primitive's effective
 * floor distance equals {@link targetEffectiveFloor}.
 *
 * Inverse of {@link getShapePrimitiveEffectiveFloorDistance}: lets the user
 * type a desired floor distance (possibly negative) and converts it to the
 * correct pivot elevation for storage.
 */
export function shapeBaseElevationFromEffectiveFloor(
  symbol: SymbolType,
  targetEffectiveFloor: number,
): number {
  const pitch = symbol.threeD?.rotation3D?.pitch ?? 0;
  const roll = symbol.threeD?.rotation3D?.roll ?? 0;
  if (Math.abs(pitch) < 1e-8 && Math.abs(roll) < 1e-8) {
    return targetEffectiveFloor;
  }
  const corners = getShapePrimitiveLocalCorners(symbol);
  if (corners.length === 0) return targetEffectiveFloor;
  const minLocalZ = computeMinLocalZAfterRotation(corners, pitch, roll);
  if (!Number.isFinite(minLocalZ)) return targetEffectiveFloor;
  // baseElevation + minLocalZ = targetEffectiveFloor  =>  baseElevation = targetEffectiveFloor - minLocalZ
  return targetEffectiveFloor - minLocalZ;
}

/**
 * Returns true when a USER_MODEL_3D SVG symbol has non-trivial 3D placement
 * that changes its top-down footprint (pitch, roll, yaw, base elevation, or
 * wall-mount). Mirrors the logic of {@link shouldShowShapePrimitiveProjection}
 * but scoped to user-uploaded 3D models.
 */
export function shouldShowUserModel3DProjection(symbol: SymbolType): boolean {
  if (symbol.libraryKey !== "USER_MODEL_3D") return false;
  const placementMode = symbol.threeD?.placementMode ?? "floor";
  const pitch = symbol.threeD?.rotation3D?.pitch ?? 0;
  const roll = symbol.threeD?.rotation3D?.roll ?? 0;
  const yaw = symbol.threeD?.rotation3D?.yaw ?? 0;
  const baseElevation = symbol.threeD?.baseElevation ?? 0;
  return (
    placementMode !== "floor" ||
    Math.abs(pitch) > 1e-4 ||
    Math.abs(roll) > 1e-4 ||
    Math.abs(yaw) > 1e-4 ||
    baseElevation > 1e-4
  );
}

/**
 * Computes the top-down convex-hull footprint polygon for a USER_MODEL_3D
 * symbol that has been pitched, rolled, yawed, or elevated in 3D.
 *
 * Mirrors the exact transform order used by the live 3D renderer
 * ({@link computeFloorplannerAssetTransform} + the asset's rotation/outer
 * symbol groups in `LivingRoomPrimitive3DMeshes`):
 *   1. Rotate the raw local-space corner by pitch/roll/yaw (about the
 *      model's own local origin — NOT its bounding-box center, since that's
 *      where the 3D rotation group's pivot actually sits).
 *   2. Scale per-axis to fit the declared footprint/height (footprint fit
 *      keeps aspect ratio; stretchFootprint scales X/Y independently).
 *   3. Translate to re-center the (unrotated) footprint at the origin and
 *      place its lowest point at `baseElevation`.
 *   4. Apply the symbol's 2D flip, plan rotation, and world position.
 *
 * When real asset bounds aren't available (e.g. still loading), falls back
 * to a symmetric box of size svgLength x svgHeight x modelHeight, pivoting
 * at its own bottom-center — a reasonable approximation matching
 * {@link computeBoxEffectiveFloorDistance}'s fallback behavior.
 */
export function createProjectedUserModel3DFootprintPolygon(
  symbol: SymbolType,
  svgLength: number,
  svgHeight: number,
): THREE.Vector2[] | null {
  if (!(svgLength > 0) || !(svgHeight > 0)) return null;

  const modelHeight =
    symbol.threeD?.height ??
    symbol.threeD?.dimensions?.height ??
    symbol.threeD?.userModel?.dimensions?.height ??
    1;
  const baseElevation = symbol.threeD?.baseElevation ?? 0;
  const rotation3D = symbol.threeD?.rotation3D;
  const bounds = symbol.threeD?.userModel?.bounds;
  const lockAspectRatio = isSymbolAspectRatioLocked(
    "lockAspectRatio" in symbol
      ? (symbol as { lockAspectRatio?: boolean }).lockAspectRatio
      : undefined,
  );
  const fitMode: "footprint" | "stretchFootprint" = lockAspectRatio
    ? "footprint"
    : "stretchFootprint";

  const localCorners = bounds
    ? computeUserModelAssetLocalFootprintCorners(
        bounds,
        { width: svgLength, depth: svgHeight, height: modelHeight },
        baseElevation,
        fitMode,
        rotation3D,
      )
    : computeUserModelBoxLocalFootprintCorners(
        svgLength / 2,
        svgHeight / 2,
        modelHeight,
        baseElevation,
        rotation3D,
      );
  if (localCorners.length === 0) return null;

  const [flipX, flipY] = getSymbol3DPlanScale(symbol);
  const planRotation = symbol.rotation ?? 0;
  const cosR = Math.cos(planRotation);
  const sinR = Math.sin(planRotation);

  const worldPoints = localCorners.map((corner) => {
    const x = corner.x * flipX;
    const y = corner.y * flipY;
    return new THREE.Vector2(
      symbol.position.x + x * cosR - y * sinR,
      symbol.position.y + x * sinR + y * cosR,
    );
  });

  return createConvexHull(worldPoints);
}

/**
 * Computes the 8 asset bounding-box corners in the outer symbol group's
 * local space (i.e. after 3D rotation, per-axis fit scale, and base
 * elevation, but before 2D flip / plan rotation / world position).
 */
function computeUserModelAssetLocalFootprintCorners(
  bounds: Product3DAssetBounds,
  targetDimensions: { width: number; depth: number; height: number },
  baseElevation: number,
  fitMode: "footprint" | "stretchFootprint",
  rotation3D: { pitch?: number; roll?: number; yaw?: number } | undefined,
): THREE.Vector3[] {
  const min = bounds.min;
  const max = bounds.max;
  if (!min || !max) return [];

  const minVec = new THREE.Vector3(min[0], min[1], min[2]);
  const maxVec = new THREE.Vector3(max[0], max[1], max[2]);
  const size = maxVec.clone().sub(minVec);
  const center = minVec.clone().add(maxVec).multiplyScalar(0.5);

  const footprintScaleX = safePositiveRatio(targetDimensions.width, size.x);
  const footprintScaleY = safePositiveRatio(targetDimensions.depth, size.y);
  const heightScale = safePositiveRatio(targetDimensions.height, size.z);
  const uniformFootprintScale = Math.min(footprintScaleX, footprintScaleY);
  const scale =
    fitMode === "stretchFootprint"
      ? new THREE.Vector3(footprintScaleX, footprintScaleY, heightScale)
      : new THREE.Vector3(
          uniformFootprintScale,
          uniformFootprintScale,
          heightScale,
        );

  const translation = new THREE.Vector3(
    -center.x * scale.x,
    -center.y * scale.y,
    baseElevation - minVec.z * scale.z,
  );

  const rotation = new THREE.Euler(
    rotation3D?.pitch ?? 0,
    rotation3D?.roll ?? 0,
    rotation3D?.yaw ?? 0,
    "XYZ",
  );

  return createBoundsCornerSet(minVec, maxVec).map((corner) =>
    corner.applyEuler(rotation).multiply(scale).add(translation),
  );
}

/**
 * Fallback footprint corners when no real asset bounds are known: a
 * symmetric box spanning (±halfWidth, ±halfDepth, 0..height), rotated in
 * place about its own bottom-center and elevated by baseElevation.
 */
function computeUserModelBoxLocalFootprintCorners(
  halfWidth: number,
  halfDepth: number,
  height: number,
  baseElevation: number,
  rotation3D: { pitch?: number; roll?: number; yaw?: number } | undefined,
): THREE.Vector3[] {
  const rotation = new THREE.Euler(
    rotation3D?.pitch ?? 0,
    rotation3D?.roll ?? 0,
    rotation3D?.yaw ?? 0,
    "XYZ",
  );
  const elevation = new THREE.Vector3(0, 0, baseElevation);
  return createBoxCorners(halfWidth, halfDepth, height).map((corner) =>
    corner.applyEuler(rotation).add(elevation),
  );
}

function createBoundsCornerSet(
  min: THREE.Vector3,
  max: THREE.Vector3,
): THREE.Vector3[] {
  return [
    new THREE.Vector3(min.x, min.y, min.z),
    new THREE.Vector3(min.x, min.y, max.z),
    new THREE.Vector3(min.x, max.y, min.z),
    new THREE.Vector3(min.x, max.y, max.z),
    new THREE.Vector3(max.x, min.y, min.z),
    new THREE.Vector3(max.x, min.y, max.z),
    new THREE.Vector3(max.x, max.y, min.z),
    new THREE.Vector3(max.x, max.y, max.z),
  ];
}

function safePositiveRatio(
  target: number | undefined,
  source: number,
): number {
  const MIN_SIZE = 0.01;
  const safeTarget =
    typeof target === "number" && Number.isFinite(target) && target > 0
      ? target
      : MIN_SIZE;
  const safeSource =
    Number.isFinite(source) && source > 0 ? source : MIN_SIZE;
  return safeTarget / safeSource;
}

/**
 * Computes the effective "distance from floor" for a box-shaped object
 * (used for SVG/GLB models where only the declared dimensions are known).
 *
 * The box spans (±halfWidth, ±halfDepth, 0..height) in the rotation group's
 * local space, with Z=0 corresponding to the model's bottom face at
 * {@link baseElevation} in the parent's coordinate space.
 */
export function computeBoxEffectiveFloorDistance(
  baseElevation: number,
  rotation3D: { pitch?: number; roll?: number } | undefined,
  halfWidth: number,
  halfDepth: number,
  height: number,
): number {
  const pitch = rotation3D?.pitch ?? 0;
  const roll = rotation3D?.roll ?? 0;
  if (Math.abs(pitch) < 1e-8 && Math.abs(roll) < 1e-8) {
    return baseElevation;
  }
  const corners = createBoxCorners(halfWidth, halfDepth, height);
  const minLocalZ = computeMinLocalZAfterRotation(corners, pitch, roll);
  return Number.isFinite(minLocalZ) ? baseElevation + minLocalZ : baseElevation;
}

/**
 * Back-computes the raw baseElevation for a box-shaped object so its
 * effective floor distance equals {@link targetEffectiveFloor}.
 */
export function boxBaseElevationFromEffectiveFloor(
  targetEffectiveFloor: number,
  rotation3D: { pitch?: number; roll?: number } | undefined,
  halfWidth: number,
  halfDepth: number,
  height: number,
): number {
  const pitch = rotation3D?.pitch ?? 0;
  const roll = rotation3D?.roll ?? 0;
  if (Math.abs(pitch) < 1e-8 && Math.abs(roll) < 1e-8) {
    return targetEffectiveFloor;
  }
  const corners = createBoxCorners(halfWidth, halfDepth, height);
  const minLocalZ = computeMinLocalZAfterRotation(corners, pitch, roll);
  if (!Number.isFinite(minLocalZ)) return targetEffectiveFloor;
  return targetEffectiveFloor - minLocalZ;
}

function createBoxCorners(
  halfWidth: number,
  halfDepth: number,
  height: number,
): THREE.Vector3[] {
  return [
    new THREE.Vector3(-halfWidth, -halfDepth, 0),
    new THREE.Vector3(halfWidth, -halfDepth, 0),
    new THREE.Vector3(halfWidth, halfDepth, 0),
    new THREE.Vector3(-halfWidth, halfDepth, 0),
    new THREE.Vector3(-halfWidth, -halfDepth, height),
    new THREE.Vector3(halfWidth, -halfDepth, height),
    new THREE.Vector3(halfWidth, halfDepth, height),
    new THREE.Vector3(-halfWidth, halfDepth, height),
  ];
}
